import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { class: "shrink-0 rounded bg-gray-200 px-1.5 py-0.5 font-bold text-gray-600" }
const _hoisted_3 = { class: "min-w-0 break-words" }
const _hoisted_4 = { class: "font-semibold" }
const _hoisted_5 = {
  key: 0,
  class: "ml-1 font-semibold"
}
const _hoisted_6 = { class: "text-gray-500" }

import { computed } from 'vue'

import CollapsibleSection from './CollapsibleSection.vue'
import { EFFECTIVE_SOURCE_KIND_LABELS } from '@/constants/ds-attendance.constants'
import { formatHourMinuteInOffset } from '@/utils/date'
import type {
  StationRegistrationSourceOutcome,
  StationRegistrationSupersededSource
} from '@/Network/Services/StationService'

/**
 * Veredito de uma fonte que NÃO venceu o ranking daquela perna.
 *
 * O contrato mora no `StationService` (é `GET /stations/:id/registration` quem o
 * devolve); aqui fica o apelido local. Os campos opcionais são de propósito: a
 * F1 grava um histórico mais pobre — sem `rank`/`outcome` a linha continua
 * legível ("— superada no ranking"), que é melhor do que esconder a fonte.
 */
export type SupersededSource = StationRegistrationSupersededSource


export default /*#__PURE__*/_defineComponent({
  __name: 'StationSourceHistoryAccordion',
  props: {
    sources: {},
    testId: { default: 'station-source-history-accordion' },
    timezone: { default: '-03:00' }
  },
  setup(__props: any) {

const props = __props;



/**
 * `WON` é a fonte VENCEDORA — ela já aparece em destaque no corpo da perna. O
 * histórico cru do backend (`history[]`) traz as três caixas juntas, então o
 * filtro fica aqui: quem monta a lista pode passar o histórico inteiro sem
 * precisar conhecer o vocabulário de vereditos.
 */
const items = computed(() => props.sources.filter((item) => item.outcome !== 'WON'))

const countOf = (outcome: StationRegistrationSourceOutcome) =>
  items.value.filter((item) => (item.outcome ?? 'SUPERSEDED') === outcome).length

const title = computed(() => {
  const superseded = countOf('SUPERSEDED') + countOf('NO_RECORD')
  const discarded = countOf('DISCARDED')
  const parts: string[] = []
  if (superseded) parts.push(`${superseded} ${superseded === 1 ? 'superada' : 'superadas'}`)
  if (discarded) parts.push(`${discarded} ${discarded === 1 ? 'descartada' : 'descartadas'}`)
  return `Histórico de fontes (${parts.join(' · ')})`
})

const OUTCOME_REASONS: Record<NonNullable<SupersededSource['outcome']>, string> = {
  WON: 'venceu o ranking',
  SUPERSEDED: 'superada no ranking',
  DISCARDED: 'descartada',
  NO_RECORD: 'sem registro na perna'
}

const OUTCOME_LABELS: Record<NonNullable<SupersededSource['outcome']>, string> = {
  WON: 'venceu',
  SUPERSEDED: 'superada',
  DISCARDED: 'descartada',
  NO_RECORD: 'sem registro'
}

const OUTCOME_CLASSES: Record<NonNullable<SupersededSource['outcome']>, string> = {
  WON: 'bg-green-50 text-sucess',
  SUPERSEDED: 'bg-yellow-50 text-warning',
  DISCARDED: 'bg-red-50 text-negative',
  NO_RECORD: 'bg-gray-200 text-gray-600'
}

const outcomeOf = (item: SupersededSource): NonNullable<SupersededSource['outcome']> =>
  item.outcome ?? 'SUPERSEDED'

const rankLabel = (item: SupersededSource): string => {
  if (item.rank == null) return '—'
  // `99` é o código da F1 para "fora do ranking configurado na escala".
  return item.rank === 99 ? '#—' : `#${item.rank}`
}

const sourceLabel = (item: SupersededSource): string =>
  item.label || EFFECTIVE_SOURCE_KIND_LABELS[item.source] || item.source

const timeLabel = (item: SupersededSource): string | null =>
  item.value ? formatHourMinuteInOffset(item.value, props.timezone) : null

const outcomeLabel = (item: SupersededSource): string => OUTCOME_LABELS[outcomeOf(item)]

const outcomeClass = (item: SupersededSource): string => OUTCOME_CLASSES[outcomeOf(item)]

const reasonLabel = (item: SupersededSource): string =>
  item.reason || OUTCOME_REASONS[outcomeOf(item)]

return (_ctx: any,_cache: any) => {
  return (items.value.length)
    ? (_openBlock(), _createBlock(CollapsibleSection, {
        key: 0,
        title: title.value,
        "test-id": _ctx.testId,
        "default-expanded": false
      }, {
        default: _withCtx(() => [
          _createElementVNode("ul", {
            class: "flex flex-col gap-1.5 list-none p-0 m-0",
            "data-testid": `${_ctx.testId}-list`
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: `${item.source}-${index}`,
                class: "flex items-start gap-2 rounded border border-gray-30 bg-gray-50 px-2 py-1.5 text-xs text-left",
                "data-testid": "station-source-history-item"
              }, [
                _createElementVNode("span", _hoisted_2, _toDisplayString(rankLabel(item)), 1),
                _createElementVNode("span", _hoisted_3, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(sourceLabel(item)), 1),
                  (timeLabel(item))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(timeLabel(item)), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    class: _normalizeClass(["ml-1 rounded px-1 py-0.5 font-semibold uppercase", outcomeClass(item)]),
                    "data-testid": "station-source-history-outcome"
                  }, _toDisplayString(outcomeLabel(item)), 3),
                  _createElementVNode("span", _hoisted_6, " — " + _toDisplayString(reasonLabel(item)), 1)
                ])
              ]))
            }), 128))
          ], 8, _hoisted_1)
        ]),
        _: 1
      }, 8, ["title", "test-id"]))
    : _createCommentVNode("", true)
}
}

})